import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { CalendarDays, ChevronRight, Columns3, List, Plus, Search, UserPlus } from 'lucide-react'
import { useState } from 'react'
import { NewStarterDialog } from '@/components/onboarding/new-starter-dialog'
import { ProgressBar, STAGE_DOT, StarterStatusBadge } from '@/components/onboarding/status'
import { PageHeader } from '@/components/shell/page'
import { Button } from '@/components/ui/button'
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { ukDate } from '@/lib/quotes'
import { can } from '@/lib/roles'
import {
  STATUS_LABEL,
  startersQuery,
  type StarterStatus,
  type StarterSummary,
} from '@/lib/starters'
import { useTitle } from '@/lib/use-title'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_app/onboarding/')({ component: OnboardingPage })

type View = 'list' | 'kanban'
const VIEW_KEY = 'hcd.onboarding.view'
const STAGES: StarterStatus[] = ['invited', 'in_progress', 'submitted', 'approved', 'withdrawn']

function storedView(): View {
  try {
    return localStorage.getItem(VIEW_KEY) === 'kanban' ? 'kanban' : 'list'
  } catch {
    return 'list'
  }
}

function OnboardingPage() {
  useTitle('Onboarding')
  const { me } = Route.useRouteContext()
  const [q, setQ] = useState('')
  const [view, setView] = useState<View>(storedView)
  const [adding, setAdding] = useState(false)
  const [showClosed, setShowClosed] = useState(false)
  const allowed = can.managePeople(me)
  const starters = useQuery({ ...startersQuery(q.trim()), enabled: allowed })

  if (!allowed)
    return (
      <>
        <PageHeader title="Onboarding" />
        <p className="rounded-lg border bg-muted p-4 text-[15px]">
          New starters' details are private. Ask an owner or HR admin if you need access.
        </p>
      </>
    )

  const choose = (v: View) => {
    setView(v)
    try {
      localStorage.setItem(VIEW_KEY, v)
    } catch {
      /* private mode */
    }
  }
  const all = starters.data ?? []
  const rows = showClosed ? all : all.filter((s) => s.status !== 'withdrawn')

  return (
    <>
      <PageHeader
        title="Onboarding"
        description="New starters complete their pack on their phone: contract, forms, policies and right to work."
        actions={
          <Button className="h-10" onClick={() => setAdding(true)}>
            <Plus />
            New starter
          </Button>
        }
      />
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <Search
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            type="search"
            aria-label="Search starters"
            placeholder="Search by name, email, role or site"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="h-11 bg-card pl-9 text-base md:text-[15px]"
          />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="size-4 accent-(--primary)"
            checked={showClosed}
            onChange={(e) => setShowClosed(e.target.checked)}
          />
          Show withdrawn
        </label>
        <div
          role="radiogroup"
          aria-label="View"
          className="inline-flex w-full rounded-lg border bg-muted p-1 lg:w-auto"
        >
          {(
            [
              ['list', 'List', List],
              ['kanban', 'Kanban', Columns3],
            ] as const
          ).map(([key, label, Icon]) => (
            <button
              key={key}
              type="button"
              role="radio"
              aria-checked={view === key}
              onClick={() => choose(key)}
              className={cn(
                'inline-flex h-9 flex-1 items-center justify-center gap-1.5 rounded-md px-3 text-sm font-medium lg:flex-none',
                view === key
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <Icon className="size-4" aria-hidden />
              {label}
            </button>
          ))}
        </div>
      </div>

      {starters.isError ? (
        <p role="alert" className="text-destructive">
          {starters.error.message}
        </p>
      ) : starters.isPending ? (
        <div className="grid gap-2">
          <Skeleton className="h-20" />
          <Skeleton className="h-20" />
        </div>
      ) : rows.length === 0 && view === 'list' ? (
        <Empty className="rounded-xl border bg-card py-12">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <UserPlus />
            </EmptyMedia>
            <EmptyTitle>{q ? 'No starters match' : 'No new starters yet'}</EmptyTitle>
            <EmptyDescription>
              {q
                ? 'Try a different name.'
                : 'Add a starter and they get one private link for their contract, forms and documents.'}
            </EmptyDescription>
          </EmptyHeader>
          {!q && (
            <EmptyContent>
              <Button className="h-10" onClick={() => setAdding(true)}>
                <Plus />
                New starter
              </Button>
            </EmptyContent>
          )}
        </Empty>
      ) : view === 'kanban' ? (
        <StarterKanban rows={rows} stages={showClosed ? STAGES : STAGES.slice(0, 4)} />
      ) : (
        <StarterList rows={rows} />
      )}
      <NewStarterDialog open={adding} onOpenChange={setAdding} />
    </>
  )
}

function nextStep(s: StarterSummary) {
  if (s.status === 'withdrawn') return `Withdrawn ${ukDate(s.withdrawnAt)}`
  if (s.status === 'approved') return `Approved ${ukDate(s.approvedAt)} by ${s.approvedBy}`
  if (s.status === 'submitted') return `Sent ${ukDate(s.submittedAt)}: ready to review`
  if (s.linkExpired) return 'Link expired: send a new one'
  if (s.progress.rejected) return 'Waiting for them to fix returned items'
  if (!s.firstOpenedAt) return `Invited ${ukDate(s.createdAt)}, not opened yet`
  return `Opened ${ukDate(s.firstOpenedAt)}`
}

function StarterList({ rows }: { rows: StarterSummary[] }) {
  return (
    <ul className="divide-y rounded-xl border bg-card">
      {rows.map((s) => (
        <li key={s.id}>
          <Link
            to="/onboarding/$id"
            params={{ id: s.id }}
            className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-2 px-4 py-3.5 hover:bg-secondary/50 md:grid-cols-[minmax(0,1fr)_minmax(0,12rem)_minmax(0,13rem)_auto]"
          >
            <span className="min-w-0">
              <span className="block truncate font-medium">{s.name}</span>
              <span className="block truncate text-sm text-muted-foreground">
                {[s.jobTitle, s.site].filter(Boolean).join(' · ') || 'No role set'}
              </span>
            </span>
            <span className="col-span-2 md:col-span-1">
              <ProgressBar p={s.progress} />
            </span>
            <span className="col-span-2 grid text-sm text-muted-foreground md:col-span-1">
              {s.startDate && (
                <span className="inline-flex items-center gap-1">
                  <CalendarDays className="size-3.5" aria-hidden />
                  Starts {ukDate(s.startDate)}
                </span>
              )}
              <span className="truncate">{nextStep(s)}</span>
            </span>
            <span className="col-start-2 row-start-1 flex items-center gap-2 md:col-start-auto md:row-start-auto">
              <StarterStatusBadge status={s.status} />
              <ChevronRight className="size-4 text-muted-foreground max-md:hidden" aria-hidden />
            </span>
          </Link>
        </li>
      ))}
    </ul>
  )
}

function StarterKanban({ rows, stages }: { rows: StarterSummary[]; stages: StarterStatus[] }) {
  return (
    <div
      className="-mx-4 overflow-x-auto px-4 pb-2 sm:mx-0 sm:px-0"
      role="region"
      aria-label="Starters by stage"
      tabIndex={0}
    >
      <ol className="grid w-max snap-x snap-mandatory auto-cols-[minmax(16rem,80vw)] grid-flow-col gap-3 sm:auto-cols-[17rem] xl:w-full xl:auto-cols-[minmax(0,1fr)]">
        {stages.map((stage) => {
          const items = rows.filter((s) => s.status === stage)
          return (
            <li
              key={stage}
              className="flex min-w-0 snap-start flex-col rounded-xl border bg-secondary/40"
              aria-labelledby={`stage-${stage}`}
            >
              <header className="flex items-center gap-2 border-b px-3 py-3">
                <span className={cn('size-2.5 rounded-full', STAGE_DOT[stage])} aria-hidden />
                <h2 id={`stage-${stage}`} className="text-sm font-semibold">
                  {STATUS_LABEL[stage]}
                </h2>
                <span className="tabular ml-auto rounded-full bg-card px-2 py-0.5 text-xs font-semibold">
                  {items.length}
                </span>
              </header>
              <ul className="grid content-start gap-2 p-2">
                {items.length === 0 ? (
                  <li className="rounded-lg border border-dashed px-3 py-6 text-center text-[13px] text-muted-foreground">
                    Nobody here
                  </li>
                ) : (
                  items.map((s) => (
                    <li key={s.id}>
                      <Link
                        to="/onboarding/$id"
                        params={{ id: s.id }}
                        className="grid gap-2 rounded-xl border bg-card p-3 hover:border-primary/40"
                      >
                        <span className="min-w-0">
                          <span className="block truncate font-semibold">{s.name}</span>
                          <span className="block truncate text-[13px] text-muted-foreground">
                            {s.jobTitle || 'No role set'}
                            {s.startDate && ` · starts ${ukDate(s.startDate)}`}
                          </span>
                        </span>
                        <ProgressBar p={s.progress} />
                      </Link>
                    </li>
                  ))
                )}
              </ul>
            </li>
          )
        })}
      </ol>
    </div>
  )
}
